import React, { useContext, useMemo, useState } from "react";
import SocialMediaIcons from "./SocialMediaIcons";
import BadgeStores from "./BadgeStores";
import SecurityIcons from "./SecurityIcons";
import EssentialLinks from "./EssentialLinks";
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionTrigger,
  AccordionContent,
} from "@radix-ui/react-accordion";
import { AnimatePresence, motion } from "framer-motion";
import { FooterContext } from "@contexts/FooterContext";
import { FooterColumn } from "src/assets/Types/Redesign/FooterDataType.type";
import Plus from "../SVGs/footer-symbols/Plus";
import Minus from "../SVGs/footer-symbols/Minus";

const FooterMobile: React.FC = () => {
  const { logo, column, onClick } = useContext(FooterContext);

  const [optionsOpen, setOptionsOpen] = useState<string[]>([]);

  const handleOpen = (key: string[]) => {
    setOptionsOpen(key);
  };

  const newColumns = useMemo(() => {
    const newList = [] as FooterColumn[];

    for (let i = 0; i < column?.length; i++) {
      newList.push(column[i]);

      if (
        newList[i].extraColumn &&
        Object.keys(newList[i].extraColumn)?.length > 0
      ) {
        newList.push(column[i].extraColumn);
      }
    }

    return newList.filter(item => item?.title);
  }, [column]);

  return (
    <div className="hidden md:block">
      <div className="w-full flex justify-between pb-12">
        <a
          href={logo?.imageLink?.page?.value?.data?.url || logo?.imageLink?.url}
          onClick={onClick}
        >
          <img
            src={logo?.image}
            alt={logo?.imageAltText}
            className="w-[42.22px] h-[40px]"
          />
        </a>
        <SocialMediaIcons />
      </div>
      <div className="pb-[96px] sm:pb-20">
        <Accordion
          type="multiple"
          value={optionsOpen}
          onValueChange={handleOpen}
        >
          {newColumns?.map((item, key) => {
            const isOpen = optionsOpen.includes(key.toString());

            const isLastItem = key === newColumns?.length - 1;

            return (
              <AccordionItem
                value={key.toString()}
                key={key}
                className={`w-full border-solid border-b-[1.5px] pb-2 border-forest-900 ${
                  !isLastItem ? `${isOpen ? "mb-3 " : "mb-6 sm:mb-4"}` : "mb-0"
                }`}
              >
                <AccordionTrigger asChild className="w-full ">
                  <div className="flex justify-between items-center">
                    <AccordionHeader>
                      <span className="text-base font-bold leading-[145%] ">
                        {item?.title}
                      </span>
                    </AccordionHeader>
                    <button
                      type="button"
                      className="border-none !bg-transparent button-no-styling !px-0"
                    >
                      {!isOpen ? <Plus /> : <Minus />}
                    </button>
                  </div>
                </AccordionTrigger>
                <AccordionContent forceMount className="pt-2">
                  <AnimatePresence initial={false} exitBeforeEnter>
                    {isOpen && (
                      <motion.div
                        initial="closed"
                        animate="open"
                        exit="closed"
                        variants={{
                          open: { opacity: 1, height: "auto" },
                          closed: { opacity: 0, height: 0 },
                        }}
                        transition={{
                          duration: 0.2,
                        }}
                        className="overflow-hidden"
                      >
                        {item?.footerLinks?.map((item, key) => {
                          return (
                            <div className="`w-full pb-3" key={key}>
                              <a
                                target={
                                  item.url?.includes("http")
                                    ? "_blank"
                                    : undefined
                                }
                                rel="noopener noreferrer"
                                href={item.page?.value?.data?.url || item.url}
                                className="body--text-regular  no-underline"
                                onClick={
                                  item.url?.includes("http")
                                    ? () => {}
                                    : onClick
                                }
                              >
                                {item.copy}
                              </a>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
      <BadgeStores />
      <p className="body--text-small pt-6 pb-12 flex justify-center text-center sm:pt-4">
        Proudly made in Santa Monica, CA © {new Date().getFullYear()} <br />{" "}
        SimplePractice, LLC
      </p>
      <SecurityIcons />
      <EssentialLinks />
    </div>
  );
};

export default FooterMobile;
